import { useMemo, useState } from "react";

import { LoopExpanded, LoopRouteResponseExpanded, getLoopSlippage } from "@bridgesplit/abf-react";
import { Hidable, percentUiToDecimals } from "@bridgesplit/utils";
import {
    Column,
    Row,
    StatColumn,
    Text,
    TooltipText,
    ToggleChevron,
    StatProps,
    TextVariant,
    TextColor
} from "@bridgesplit/ui";
import { TokenListMetadata, LoopWindType } from "@bridgesplit/abf-sdk";
import { Collapse, Divider } from "@mui/material";

import { LoopDisplayType } from "../../common/loop";
import { ExecutionDetailsLabels, LoopExecutionDetails } from "./types";
import { SwapPriceDifferenceDisplay } from "./SwapPriceDifference";
import { useExecutionDetailsLabels } from "./utils";
import { formatExecutionLossValue, formatTokenValue } from "./formatters";
import { ReceiveOnCloseDisplay } from "./ReceiveOnClose";

export function ExecutionDetails({
    displayType,
    setDisplayTypeHandler,
    executionDetails,
    receiveTokenAmount,
    leftOverPrincipal,
    slippagePercentDecimals,
    loading,
    displayTokenType,
    externalQuote,
    collateralUsdPrice,
    principalUsdPrice,
    principalToken,
    collateralToken,
    labelOverrides,
    textVariant,
    loopExpanded
}: {
    displayType: LoopDisplayType;
    setDisplayTypeHandler: () => void;
    executionDetails: LoopExecutionDetails | undefined;
    receiveTokenAmount: number | undefined;
    leftOverPrincipal: number | undefined;
    slippagePercentDecimals: number | undefined;
    loading: boolean;
    displayTokenType: LoopWindType | undefined;
    externalQuote: LoopRouteResponseExpanded | undefined;
    collateralUsdPrice: number | undefined;
    principalUsdPrice: number | undefined;
    principalToken: TokenListMetadata | undefined;
    collateralToken: TokenListMetadata | undefined;
    labelOverrides?: Partial<ExecutionDetailsLabels>;
    textVariant: TextVariant;
    loopExpanded: LoopExpanded | undefined;
}) {
    const [isExpanded, setIsExpanded] = useState(false);

    const executionDetailsLabels = useExecutionDetailsLabels({ overrides: labelOverrides });

    const executionErrorImpactColor = getExecutionErrorImpactColor({ executionDetails, loopExpanded });

    const executionDetailsStats = useExecutionDetailsStats({
        slippagePercentDecimals,
        displayTokenType,
        displayType,
        setDisplayTypeHandler,
        externalQuote,
        executionDetails,
        receiveTokenAmount,
        leftOverPrincipal,
        collateralUsdPrice,
        principalUsdPrice,
        principalToken,
        collateralToken,
        executionDetailsLabels,
        textVariant,
        executionErrorImpactColor
    });

    return (
        <Column>
            <ExecutionDetailsCollapableHeader
                displayType={displayType}
                setDisplayTypeHandler={setDisplayTypeHandler}
                executionDetails={executionDetails}
                receiveTokenAmount={receiveTokenAmount}
                leftOverPrincipal={leftOverPrincipal}
                slippagePercentDecimals={slippagePercentDecimals}
                loading={loading}
                displayTokenType={displayTokenType}
                externalQuote={externalQuote}
                collateralUsdPrice={collateralUsdPrice}
                principalUsdPrice={principalUsdPrice}
                principalToken={principalToken}
                collateralToken={collateralToken}
                isExpanded={isExpanded}
                setIsExpanded={setIsExpanded}
                executionDetailsTooltip={executionDetailsLabels.executionImpact?.tooltip}
                textVariant={textVariant}
                executionErrorImpactColor={executionErrorImpactColor}
            />
            <Collapse in={isExpanded}>
                <Column sx={{ mt: 1 }} spacing={1}>
                    {!loading && <Divider />}
                    <StatColumn loading={loading} variant={textVariant} spacing={0.5} stats={executionDetailsStats} />
                </Column>
            </Collapse>
        </Column>
    );
}

function getExecutionErrorImpactColor({
    executionDetails,
    loopExpanded
}: {
    executionDetails: LoopExecutionDetails | undefined;
    loopExpanded: LoopExpanded | undefined;
}): TextColor | undefined {
    if (!executionDetails || !loopExpanded) return undefined;

    // Handle edge case: positive execution amounts (gains) should use default text color
    // Success color is handled explicity.
    // This function is specifically for styling execution losses/fees/slippage
    if (executionDetails.executionLossAmount >= 0) return "body";

    const absoluteLoss = Math.abs(executionDetails.executionLossAmount);
    const thresholds = getLoopSlippage(loopExpanded).slippagePresetsUi.map(
        (slippage) => percentUiToDecimals(slippage) * executionDetails.fairMarketValue
    );

    if (absoluteLoss >= thresholds[2]) return "error"; // High impact: red
    if (absoluteLoss >= thresholds[0]) return "warning"; // Medium/low impact: warning

    return "body"; // Minimal impact: default color
}

function ExecutionDetailsCollapableHeader({
    displayType,
    setDisplayTypeHandler,
    executionDetails,
    loading,
    displayTokenType,
    collateralUsdPrice,
    principalUsdPrice,
    principalToken,
    collateralToken,
    isExpanded,
    setIsExpanded,
    executionDetailsTooltip,
    textVariant,
    executionErrorImpactColor
}: {
    displayType: LoopDisplayType;
    setDisplayTypeHandler: () => void;
    executionDetails: LoopExecutionDetails | undefined;
    receiveTokenAmount: number | undefined;
    leftOverPrincipal: number | undefined;
    slippagePercentDecimals: number | undefined;
    loading: boolean;
    displayTokenType: LoopWindType | undefined;
    externalQuote: LoopRouteResponseExpanded | undefined;
    collateralUsdPrice: number | undefined;
    principalUsdPrice: number | undefined;
    principalToken: TokenListMetadata | undefined;
    collateralToken: TokenListMetadata | undefined;
    isExpanded: boolean;
    setIsExpanded: (isExpanded: boolean) => void;
    executionDetailsTooltip: string | undefined;
    textVariant: TextVariant;
    executionErrorImpactColor: TextColor | undefined;
}) {
    return (
        <Row sx={{ cursor: "pointer" }} spaceBetween>
            <Row spacing={1}>
                <Text color="caption" variant={textVariant} loading={loading}>
                    Execution impact
                </Text>

                {!loading && <TooltipText variant={textVariant} helpText={executionDetailsTooltip} />}
            </Row>
            <ExecutionDetailsEndValue
                loading={loading}
                executionDetails={executionDetails}
                isExpanded={isExpanded}
                displayTokenType={displayTokenType}
                displayType={displayType}
                setIsExpanded={setIsExpanded}
                setDisplayTypeHandler={setDisplayTypeHandler}
                principalToken={principalToken}
                collateralToken={collateralToken}
                principalUsdPrice={principalUsdPrice}
                collateralUsdPrice={collateralUsdPrice}
                textVariant={textVariant}
                executionErrorImpactColor={executionErrorImpactColor}
            />
        </Row>
    );
}

function ExecutionDetailsEndValue({
    loading,
    executionDetails,
    isExpanded,
    displayTokenType,
    displayType,
    setIsExpanded,
    setDisplayTypeHandler,
    principalToken,
    collateralToken,
    principalUsdPrice,
    collateralUsdPrice,
    textVariant,
    executionErrorImpactColor
}: {
    loading: boolean;
    executionDetails: LoopExecutionDetails | undefined;
    displayTokenType: LoopWindType | undefined;
    isExpanded: boolean;
    displayType: LoopDisplayType;
    setIsExpanded: (isExpanded: boolean) => void;
    setDisplayTypeHandler: () => void;
    principalToken: TokenListMetadata | undefined;
    collateralToken: TokenListMetadata | undefined;
    principalUsdPrice: number | undefined;
    collateralUsdPrice: number | undefined;
    textVariant: TextVariant;
    executionErrorImpactColor: TextColor | undefined;
}) {
    return (
        <Row spacing={1}>
            <Row spacing={1}>
                <Text
                    onClick={() => setDisplayTypeHandler()}
                    variant={textVariant}
                    loading={loading}
                    color={
                        executionDetails?.executionLossAmount && executionDetails.executionLossAmount < 0
                            ? executionErrorImpactColor
                            : "success"
                    }
                >
                    {formatExecutionLossValue({
                        executionDetails,
                        displayTokenType,
                        principalToken,
                        collateralToken,
                        principalUsdPrice,
                        collateralUsdPrice,
                        displayType
                    })}
                </Text>
            </Row>
            {!loading && (
                <ToggleChevron
                    onClick={() => setIsExpanded(!isExpanded)}
                    textVariant="body1"
                    sx={{ p: 0 }}
                    expanded={isExpanded}
                />
            )}
        </Row>
    );
}

function useExecutionDetailsStats({
    displayTokenType,
    displayType,
    setDisplayTypeHandler,
    externalQuote,
    executionDetails,
    receiveTokenAmount,
    leftOverPrincipal,
    collateralUsdPrice,
    principalUsdPrice,
    principalToken,
    collateralToken,
    executionDetailsLabels,
    textVariant,
    executionErrorImpactColor
}: {
    slippagePercentDecimals: number | undefined;
    displayTokenType: LoopWindType | undefined;
    displayType: LoopDisplayType;
    setDisplayTypeHandler: () => void;
    externalQuote: LoopRouteResponseExpanded | undefined;
    executionDetails: LoopExecutionDetails | undefined;
    receiveTokenAmount: number | undefined;
    leftOverPrincipal: number | undefined;
    collateralUsdPrice: number | undefined;
    principalUsdPrice: number | undefined;
    principalToken: TokenListMetadata | undefined;
    collateralToken: TokenListMetadata | undefined;
    executionDetailsLabels: ExecutionDetailsLabels;
    textVariant: TextVariant;
    executionErrorImpactColor: TextColor | undefined;
}) {
    const { token, tokenPrice } =
        displayTokenType === LoopWindType.Collateral
            ? { token: collateralToken, tokenPrice: collateralUsdPrice }
            : { token: principalToken, tokenPrice: principalUsdPrice };

    const executionDetailsStats = useMemo(() => {
        if (!executionDetails || !token || tokenPrice === undefined) return [];
        const stats: Hidable<StatProps>[] = [
            {
                caption: "Fair market value",
                value: (
                    <Text variant={textVariant} translate="no" onClick={() => setDisplayTypeHandler()}>
                        {formatTokenValue(executionDetails.fairMarketValue || 0, tokenPrice, token, displayType)}
                    </Text>
                ),
                tooltip: executionDetailsLabels.fairMarketValue?.tooltip
            },
            {
                caption: "Fees",
                value: (
                    <Text
                        variant={textVariant}
                        translate="no"
                        onClick={() => setDisplayTypeHandler()}
                        color={executionErrorImpactColor}
                    >
                        {formatTokenValue(-executionDetails.feeAmount, tokenPrice, token, displayType)}
                    </Text>
                ),

                tooltip: executionDetailsLabels.fees?.tooltip,
                hide: !executionDetails.feeAmount
            },
            {
                caption: "Price impact",
                value: (
                    <Text
                        variant={textVariant}
                        translate="no"
                        onClick={() => setDisplayTypeHandler()}
                        color={executionErrorImpactColor}
                    >
                        {formatTokenValue(-executionDetails.priceImpactAmount, tokenPrice, token, displayType)}
                    </Text>
                ),
                hide: !executionDetails.priceImpactAmount
            },
            {
                caption: "Slippage",
                value: (
                    <Text
                        variant={textVariant}
                        translate="no"
                        onClick={() => setDisplayTypeHandler()}
                        color={executionErrorImpactColor}
                    >
                        {formatTokenValue(-executionDetails.slippageAmount, tokenPrice, token, displayType)}
                    </Text>
                ),
                hide: !executionDetails.slippageAmount
            },
            {
                caption: "Swap difference",
                value: (
                    <SwapPriceDifferenceDisplay
                        swapAmount={externalQuote?.inputAmount}
                        executionDetails={executionDetails}
                        collateralToken={collateralToken}
                        principalToken={principalToken}
                        inputTokenPrice={tokenPrice}
                        principalTokenPrice={principalUsdPrice}
                        displayType={displayType}
                        setDisplayTypeHandler={setDisplayTypeHandler}
                        textVariant={textVariant}
                        executionErrorImpactColor={executionErrorImpactColor}
                        inputToken={token}
                    />
                ),
                tooltip: executionDetailsLabels.swapDifference?.tooltip,
                hide: !Math.abs(executionDetails.oraclePriceDeviation)
            },
            {
                caption: executionDetailsLabels.receiveOnClose?.caption || "Receive on close",
                value: (
                    <ReceiveOnCloseDisplay
                        executionDetails={executionDetails}
                        principalToken={principalToken}
                        principalUsdPrice={principalUsdPrice}
                        displayType={displayType}
                        setDisplayTypeHandler={setDisplayTypeHandler}
                        receiveTokenAmount={receiveTokenAmount}
                        leftOverPrincipal={leftOverPrincipal}
                        textVariant={textVariant}
                        receiveToken={token}
                    />
                ),
                tooltip: executionDetailsLabels.receiveOnClose?.tooltip
            }
        ].filter((s) => !s.hide);

        return stats;
    }, [
        executionDetails,
        externalQuote,
        token,
        tokenPrice,
        displayType,
        setDisplayTypeHandler,
        executionDetailsLabels,
        receiveTokenAmount,
        leftOverPrincipal,
        principalUsdPrice,
        principalToken,
        collateralToken,
        textVariant,
        executionErrorImpactColor
    ]);

    return executionDetailsStats;
}
